/** Allowed difficulty values selected for a Training Ground session. */
export type TrainingGroundDifficulty = "easy" | "medium" | "hard";

/** Learner-safe reason explaining the reward outcome for one answer. */
export type TrainingGroundRewardReason =
  | "awarded"
  | "mastered"
  | "dailyLimit"
  | "disabled"
  | "pending"
  | "unavailable"
  | "duplicate"
  | "incorrect";

/** Learner-safe reward outcome returned for one graded answer. */
export type TrainingGroundReward = {
  awarded?: boolean;
  firstMastery: boolean;
  xp: number;
  reason?: TrainingGroundRewardReason;
  remainingRewardedQuestions?: number;
};

/** Learner's post-grade reflection after seeing one graded result. */
export type TrainingGroundPostGradeConfidence = "sure" | "unsure";

/** Privacy-safe confidence calibration counts for one completed session. */
export type TrainingGroundConfidenceSummary = {
  answeredCount: number;
  recordedCount: number;
  sureCorrectCount: number;
  sureIncorrectCount: number;
  unsureCorrectCount: number;
  unsureIncorrectCount: number;
  sureAccuracy: number | null;
  unsureAccuracy: number | null;
};

/** Authoritative confidence replacement returned by one result-row mutation. */
export type TrainingGroundConfidenceMutation = {
  questionId: number;
  confidence: TrainingGroundPostGradeConfidence;
  confidenceUpdatedAt?: string | { timestamp: number } | null;
  confidenceRevision: number;
  confidenceSummary: TrainingGroundConfidenceSummary;
};

/**
 * A regular practice round, a server-owned spaced-retry review round, or a
 * server-selected adaptive round targeting the learner's current difficulty
 * band.
 */
export type TrainingGroundSessionMode =
  | "standard"
  | "mistakeReview"
  | "adaptive";

/** Learner-safe signals behind one adaptive round's question selection. */
export type TrainingGroundAdaptiveProfile = {
  recentAccuracy: number | null;
  targetDifficulty: TrainingGroundDifficulty | null;
  dueMistakeCount: number;
  unseenQuestionCount: number;
};

/** Minimal embedded label used by Training Ground filters. */
export type TrainingGroundEmbeddedItem = {
  id: number;
  name?: string;
  title?: string;
};

/** Learner-safe question content returned before final submission. */
export type TrainingGroundQuestion = {
  id: number;
  title: string;
  description?: string;
  contentType?: string;
  type: "mcq" | "boolean" | "fillInTheBlank";
  answers: Array<string | number>;
  numberOfAnswers: number;
  maxSelections: number;
  difficulty: string;
};

/** Graded answer details revealed only after the session is complete. */
export type TrainingGroundResult = {
  questionId: number;
  isCorrect: boolean;
  correctAnswer?: number | number[] | string;
  answer?: number | number[] | string;
  explanation?: string;
  reward?: TrainingGroundReward | null;
  confidence?: TrainingGroundPostGradeConfidence | null;
  confidenceUpdatedAt?: string | { timestamp: number } | null;
};

/** A choice selection or typed answer used by the Training Ground player. */
export type TrainingGroundAnswer = number | number[] | string;

/** Visual review state derived from a completed question result. */
export type TrainingGroundResultState = "correct" | "incorrect" | "unanswered";

/** Persisted Training Ground session contract consumed by the player. */
export type TrainingGroundSession = {
  id: number;
  questions: TrainingGroundQuestion[];
  answeredCount: number;
  totalQuestions: number;
  completed: boolean;
  difficulties: TrainingGroundDifficulty[];
  startedAt?: string | { timestamp: number } | null;
  endsAt?: string | { timestamp: number } | null;
  serverTime?: string | { timestamp: number } | null;
  results?: TrainingGroundResult[];
  confidenceSummary?: TrainingGroundConfidenceSummary;
  confidenceRevision?: number;
  mode?: TrainingGroundSessionMode;
  adaptiveProfile?: TrainingGroundAdaptiveProfile | null;
  awardedXp?: number;
  pendingRewardCount?: number;
  dailyLimitReached?: boolean;
  remainingRewardedQuestions?: number;
};

/** Grade-scoped subject option and its available question metadata. */
export type TrainingGroundCatalogSubject = {
  subject: TrainingGroundEmbeddedItem | null;
  questionCount: number;
  difficulties: string[];
  /** Lessons with eligible questions; `categoryIds` link them to chapters. */
  lessons?: {
    lesson: TrainingGroundEmbeddedItem | null;
    questionCount: number;
    difficulties: string[];
    categoryIds?: number[];
  }[];
  /** Categories (chapters) with eligible questions in this subject. */
  categories?: {
    category: TrainingGroundEmbeddedItem | null;
    questionCount: number;
    difficulties: string[];
  }[];
};

/** Learner-safe tenant configuration for the Training Ground setup form. */
export type TrainingGroundConfiguration = {
  enabled: boolean;
  allowedQuestionCounts: number[];
  defaultQuestionCount: number;
  allowedDifficulties: TrainingGroundDifficulty[];
  allowedTimerMinutes: number[];
  gamificationEnabled: boolean;
};

/** One completed-round summary row returned by the paginated history list. */
export type TrainingGroundSessionHistoryItem = {
  id: number;
  subject: TrainingGroundEmbeddedItem | null;
  answeredCount: number;
  totalQuestions: number;
  correctCount: number;
  accuracy: number;
  xp: number;
  awardedXp?: number;
  pendingRewardCount?: number;
  dailyLimitReached?: boolean;
  remainingRewardedQuestions?: number;
  startedAt?: string | { timestamp: number } | null;
  endsAt?: string | { timestamp: number } | null;
  durationMinutes?: number | null;
  completedAt?: string | { timestamp: number } | null;
  mode?: TrainingGroundSessionMode;
  adaptiveProfile?: TrainingGroundAdaptiveProfile | null;
};

/** One learner-safe mistake-notebook row returned by the mistakes summary. */
export type TrainingGroundMistakeSummaryItem = {
  id: number;
  subject: TrainingGroundEmbeddedItem | null;
  lesson: TrainingGroundEmbeddedItem | null;
  incorrectCount: number;
  reviewStage: number;
  lastWrongAt?: string | { timestamp: number } | null;
  lastReviewedAt?: string | { timestamp: number } | null;
  nextReviewAt?: string | { timestamp: number } | null;
  resolvedAt?: string | { timestamp: number } | null;
  due: boolean;
};

/** Learner-safe mistake-notebook overview used by the review reminder panel. */
export type TrainingGroundMistakesSummary = {
  dueCount: number;
  upcomingCount: number;
  nextReviewAt?: string | { timestamp: number } | null;
  items: TrainingGroundMistakeSummaryItem[];
};

/** Evidence state for one lesson in the learner's current eligible question bank. */
export type TrainingGroundLearningPlanEvidenceState =
  | "unseen"
  | "weak"
  | "strong"
  | "covered";

/** Spaced-review state for one lesson in the learner's current eligible question bank. */
export type TrainingGroundLearningPlanReviewState = "due" | "upcoming" | "none";

/** Aggregate current-bank evidence shown at the top of the learning plan. */
export type TrainingGroundLearningPlanTotals = {
  lessonCount: number;
  eligibleQuestions: number;
  coveredQuestions: number;
  masteredQuestions: number;
  unresolvedQuestions: number;
  dueNowQuestions: number;
  upcomingQuestions: number;
  masteryRate: number;
};

/** One paginated lesson row in the learner's current practice evidence map. */
export type TrainingGroundLearningPlanLesson = {
  key: string;
  subject: TrainingGroundEmbeddedItem | null;
  lesson: TrainingGroundEmbeddedItem | null;
  eligibleQuestions: number;
  coveredQuestions: number;
  masteredQuestions: number;
  unresolvedQuestions: number;
  dueNowQuestions: number;
  upcomingQuestions: number;
  masteryRate: number;
  evidenceState: TrainingGroundLearningPlanEvidenceState;
  reviewState: TrainingGroundLearningPlanReviewState;
  nextReviewAt?: string | { timestamp: number } | null;
};

/** One bounded lesson row in the learner's next-review plan. */
export type TrainingGroundLearningPlanReviewLesson = {
  key: string;
  subject: TrainingGroundEmbeddedItem | null;
  lesson: TrainingGroundEmbeddedItem | null;
  dueNowQuestions: number;
  upcomingQuestions: number;
  reviewState: TrainingGroundLearningPlanReviewState;
  nextReviewAt?: string | { timestamp: number } | null;
};

/** Bounded actionable review summary returned beside the full lesson map. */
export type TrainingGroundLearningPlanReview = {
  actionableDueCount: number;
  actionableUpcomingCount: number;
  nextReviewAt?: string | { timestamp: number } | null;
  unavailableUnresolvedCount: number;
  lessons: TrainingGroundLearningPlanReviewLesson[];
  totalLessonCount: number;
  hasMore: boolean;
};

/** Learner-safe, paginated lesson evidence and next-review plan. */
export type TrainingGroundLearningPlan = {
  asOf: string | { timestamp: number };
  snapshot: string;
  totals: TrainingGroundLearningPlanTotals;
  lessons: TrainingGroundLearningPlanLesson[];
  review: TrainingGroundLearningPlanReview;
  paginationInfo: TrainingGroundPaginationInfo;
};

/** Visible state of the optional learner learning-plan request. */
export type TrainingGroundLearningPlanStatus =
  | "loading"
  | "unavailable"
  | "ready";

/** One point on the learner's recent accuracy trend, in chronological order. */
export type TrainingGroundAnalyticsTrendPoint = {
  completedAt?: string | { timestamp: number } | null;
  accuracy: number;
};

/** Learner-safe accuracy breakdown for one subject across recent sessions. */
export type TrainingGroundAnalyticsSubjectBreakdown = {
  subject: TrainingGroundEmbeddedItem | null;
  totalQuestions: number;
  correctAnswers: number;
  accuracy: number;
};

/** Learner-safe accuracy breakdown for one difficulty band across recent sessions. */
export type TrainingGroundAnalyticsDifficultyBreakdown = {
  difficulty: TrainingGroundDifficulty;
  totalQuestions: number;
  correctAnswers: number;
  accuracy: number;
};

/** Learner-safe recent performance for one Training Ground session mode. */
export type TrainingGroundAnalyticsModeComparison = {
  mode: TrainingGroundSessionMode;
  completedRounds: number;
  totalQuestions: number;
  correctAnswers: number;
  accuracy: number;
  trend: TrainingGroundAnalyticsTrendPoint[];
};

/** Aggregate or single-mode view selected for the learner's accuracy trend. */
export type TrainingGroundAnalyticsModeFilter =
  | "all"
  | TrainingGroundSessionMode;

/** Learner-safe analytics overview aggregated from the learner's recent completed sessions. */
export type TrainingGroundAnalytics = {
  completedRounds: number;
  totalQuestions: number;
  correctAnswers: number;
  accuracy: number;
  totalXp: number;
  trend: TrainingGroundAnalyticsTrendPoint[];
  modes: TrainingGroundAnalyticsModeComparison[];
  subjects: TrainingGroundAnalyticsSubjectBreakdown[];
  difficulties: TrainingGroundAnalyticsDifficultyBreakdown[];
};

/** Pagination metadata returned by the Training Ground history endpoint. */
export type TrainingGroundPaginationInfo = {
  page: number;
  limit: number;
  result?: number;
  total: number;
  pages: number;
  hasNextPage?: boolean;
};

/** Distinguish an unavailable plan, an empty eligible bank, and displayable evidence. */
export function getTrainingGroundLearningPlanState(
  learningPlan: Pick<TrainingGroundLearningPlan, "totals"> | undefined,
): "unavailable" | "empty" | "ready" {
  if (!learningPlan) return "unavailable";

  return learningPlan.totals.eligibleQuestions === 0 ? "empty" : "ready";
}

/** Append a later lesson page while keeping the newest totals and review snapshot. */
export function mergeTrainingGroundLearningPlanPage(
  current: TrainingGroundLearningPlan | undefined,
  incoming: TrainingGroundLearningPlan,
  requestedPage: number,
): TrainingGroundLearningPlan {
  if (!current || requestedPage === 1) return incoming;

  const existingKeys = new Set(current.lessons.map((lesson) => lesson.key));
  const newLessons = incoming.lessons.filter(
    (lesson) => !existingKeys.has(lesson.key),
  );

  return {
    ...incoming,
    lessons: [...current.lessons, ...newLessons],
  };
}

/** Result of applying a retry or load-more response to the visible learning plan. */
export type TrainingGroundLearningPlanRequestResult = {
  learningPlan?: TrainingGroundLearningPlan;
  status: Exclude<TrainingGroundLearningPlanStatus, "loading">;
  loadMoreFailed: boolean;
  restartRequired: boolean;
};

/** Convert the backend snapshot cutoff into the exact ISO query token expected for later pages. */
export function getTrainingGroundLearningPlanAsOfToken(
  asOf: TrainingGroundLearningPlan["asOf"] | undefined,
): string | undefined {
  if (!asOf) return undefined;
  if (typeof asOf === "string") return asOf;

  return new Date(asOf.timestamp).toISOString();
}

function hasMatchingLearningPlanSnapshot(
  current: TrainingGroundLearningPlan,
  incoming: TrainingGroundLearningPlan,
): boolean {
  const currentAsOf = getTrainingGroundLearningPlanAsOfToken(current.asOf);
  const incomingAsOf = getTrainingGroundLearningPlanAsOfToken(incoming.asOf);

  if (current.snapshot !== incoming.snapshot || currentAsOf !== incomingAsOf) {
    return false;
  }

  return (
    JSON.stringify(current.totals) === JSON.stringify(incoming.totals) &&
    JSON.stringify(current.review) === JSON.stringify(incoming.review)
  );
}

/** Preserve loaded rows on a failed later page and recover an unavailable first page on retry. */
export function resolveTrainingGroundLearningPlanRequest(
  current: TrainingGroundLearningPlan | undefined,
  incoming: TrainingGroundLearningPlan | undefined,
  requestedPage: number,
): TrainingGroundLearningPlanRequestResult {
  if (
    incoming &&
    requestedPage > 1 &&
    (!current || !hasMatchingLearningPlanSnapshot(current, incoming))
  ) {
    return {
      learningPlan: current,
      status: current ? "ready" : "unavailable",
      loadMoreFailed: false,
      restartRequired: true,
    };
  }

  if (incoming) {
    return {
      learningPlan: mergeTrainingGroundLearningPlanPage(
        current,
        incoming,
        requestedPage,
      ),
      status: "ready",
      loadMoreFailed: false,
      restartRequired: false,
    };
  }

  if (requestedPage === 1) {
    return {
      status: "unavailable",
      loadMoreFailed: false,
      restartRequired: false,
    };
  }

  return {
    learningPlan: current,
    status: current ? "ready" : "unavailable",
    loadMoreFailed: true,
    restartRequired: false,
  };
}

/** Refresh evidence only after the server confirms that a submitted round completed. */
export function shouldRefreshTrainingGroundLearningPlan(
  intent: string | undefined,
  session: Pick<TrainingGroundSession, "completed"> | undefined,
): boolean {
  return intent === "submit" && session?.completed === true;
}

/** Learner-facing reward totals derived from a completed session projection. */
export type TrainingGroundRewardSummary = {
  awardedXp: number;
  pendingRewardCount: number;
  dailyLimitReached: boolean;
  remainingRewardedQuestions?: number;
};

/** Prefer server aggregates while deriving safe fallbacks from older result payloads. */
export function getTrainingGroundRewardSummary(
  session: Pick<
    TrainingGroundSession,
    | "results"
    | "awardedXp"
    | "pendingRewardCount"
    | "dailyLimitReached"
    | "remainingRewardedQuestions"
  >,
): TrainingGroundRewardSummary {
  const rewards = (session.results || [])
    .map((result) => result.reward)
    .filter((reward): reward is TrainingGroundReward => Boolean(reward));
  const remainingValues = rewards
    .map((reward) => reward.remainingRewardedQuestions)
    .filter((remaining): remaining is number => typeof remaining === "number");
  const derivedRemaining = remainingValues[remainingValues.length - 1];

  return {
    awardedXp:
      session.awardedXp ??
      rewards.reduce((total, reward) => total + (reward.xp || 0), 0),
    pendingRewardCount:
      session.pendingRewardCount ??
      rewards.filter((reward) => reward.reason === "pending").length,
    dailyLimitReached:
      session.dailyLimitReached ??
      rewards.some((reward) => reward.reason === "dailyLimit"),
    remainingRewardedQuestions:
      session.remainingRewardedQuestions ?? derivedRemaining,
  };
}

/** Return a display-safe percentage for a completed session. */
export function getTrainingGroundAccuracy(
  correctAnswers: number,
  answered: number,
) {
  return answered <= 0 ? 0 : Math.round((correctAnswers / answered) * 100);
}
/** Classify accuracy into a translation-key suffix. */
export function getTrainingGroundMastery(accuracy: number) {
  if (accuracy >= 80) return "strong";
  if (accuracy >= 50) return "developing";

  return "starting";
}

/** Identify the learner-facing state of a completed question result. */
export function getTrainingGroundResultState(
  result: TrainingGroundResult,
): TrainingGroundResultState {
  if (result.answer == null) return "unanswered";
  if (typeof result.answer === "string" && result.answer.trim().length === 0)
    return "unanswered";
  if (Array.isArray(result.answer) && result.answer.length === 0)
    return "unanswered";

  return result.isCorrect ? "correct" : "incorrect";
}

/** Convert answer indices into a readable string while omitting invalid indices. */
export function formatTrainingGroundAnswers(
  selection: TrainingGroundAnswer | undefined,
  answers: Array<string | number>,
) {
  if (typeof selection === "string") return selection;

  const indices = Array.isArray(selection)
    ? selection
    : selection == null
      ? []
      : [selection];

  return indices
    .map((index) => answers[index])
    .filter((answer): answer is string | number => answer != null)
    .map(String)
    .join(", ");
}

/** Convert an API date-like value to milliseconds. */
export function getTrainingGroundTimestamp(
  value: TrainingGroundSession["endsAt"],
) {
  if (!value) return undefined;
  if (typeof value === "object") return value.timestamp;

  const timestamp = new Date(value).getTime();

  return Number.isNaN(timestamp) ? undefined : timestamp;
}

/** Return a display-safe subject label for one completed-round history row. */
export function getTrainingGroundHistorySubjectLabel(
  subject: TrainingGroundEmbeddedItem | null,
): string | undefined {
  if (!subject) return undefined;

  return subject.name || subject.title || undefined;
}

/**
 * Resolve a requested subject id (e.g. from a `?subject=` link) against the
 * already-loaded authenticated student's catalog. Any unknown, stale, or
 * tampered id silently falls back to `undefined` so the caller preselects
 * nothing rather than trusting an unverified value.
 */
export function resolveTrainingGroundPreselectedSubjectId(
  requestedSubjectId: unknown,
  catalog: TrainingGroundCatalogSubject[],
): number | undefined {
  const numericSubjectId = Number(requestedSubjectId);

  if (!Number.isFinite(numericSubjectId) || numericSubjectId <= 0) {
    return undefined;
  }

  const isKnownSubject = catalog.some(
    (item) => item.subject?.id === numericSubjectId,
  );

  return isKnownSubject ? numericSubjectId : undefined;
}

/** An analytics summary has learner-visible data once at least one round completed. */
export function hasTrainingGroundAnalyticsData(
  analytics: Pick<TrainingGroundAnalytics, "completedRounds"> | undefined,
): boolean {
  return Boolean(analytics && analytics.completedRounds > 0);
}

/**
 * Select an aggregate or mode-specific trend without combining unlike rounds.
 *
 * @example
 * getTrainingGroundAnalyticsTrend(analytics, "adaptive");
 */
export function getTrainingGroundAnalyticsTrend(
  analytics: Pick<TrainingGroundAnalytics, "trend" | "modes">,
  mode: TrainingGroundAnalyticsModeFilter,
): TrainingGroundAnalyticsTrendPoint[] {
  if (mode === "all") return analytics.trend;

  return (
    analytics.modes.find((comparison) => comparison.mode === mode)?.trend || []
  );
}

/** A mistakes reminder is only actionable once at least one mistake is due. */
export function canStartTrainingGroundMistakeReview(
  summary: Pick<TrainingGroundMistakesSummary, "dueCount"> | undefined,
): boolean {
  return Boolean(summary && summary.dueCount > 0);
}

/** Presentation-ready values behind the concise adaptive profile explanation. */
export type TrainingGroundAdaptiveExplanation = {
  hasHistory: boolean;
  recentAccuracy: number;
  targetDifficulty: TrainingGroundDifficulty | null;
  dueMistakeCount: number;
  unseenQuestionCount: number;
};

/**
 * Reduce a learner-safe adaptive profile into the values the setup/active/
 * results screens interpolate into a single localized explanation string.
 * Returns `undefined` for a non-adaptive round so callers can skip
 * rendering the banner entirely.
 */
export function getTrainingGroundAdaptiveExplanation(
  profile: TrainingGroundAdaptiveProfile | null | undefined,
): TrainingGroundAdaptiveExplanation | undefined {
  if (!profile) return undefined;

  return {
    hasHistory: profile.recentAccuracy !== null,
    recentAccuracy: profile.recentAccuracy ?? 0,
    targetDifficulty: profile.targetDifficulty,
    dueMistakeCount: profile.dueMistakeCount,
    unseenQuestionCount: profile.unseenQuestionCount,
  };
}

/** Calculate remaining seconds against the server offset. */
export function getTrainingGroundRemainingSeconds(
  endsAt: number,
  serverClockOffset: number,
  now = Date.now(),
) {
  return Math.max(0, Math.ceil((endsAt - (now + serverClockOffset)) / 1000));
}
